import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, AtSign } from "lucide-react";

interface Props {
  onComplete: () => void;
  currentUsername?: string | null;
}

const UsernameSetup = ({ onComplete, currentUsername }: Props) => {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const [value, setValue] = useState(currentUsername || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validate = (v: string) => {
    if (v.length < 3) return t("usernameSetup.minLength");
    if (v.length > 24) return t("usernameSetup.maxLength");
    if (!/^[a-zA-Z0-9_]+$/.test(v)) return t("usernameSetup.invalidChars");
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    const err = validate(trimmed);
    if (err) { setError(err); return; }
    if (!user) return;

    setLoading(true);
    setError("");

    // Server-side profanity check
    try {
      const { data: check, error: checkErr } = await supabase.functions.invoke("validate-username", {
        body: { username: trimmed },
      });
      if (checkErr || !check?.valid) {
        setError(check?.reason === "inappropriate" ? t("usernameSetup.inappropriate") : t("common.error"));
        setLoading(false);
        return;
      }
    } catch {
      // If validation service is down, allow through (fail-open for UX)
    }

    const { error: dbError } = await (supabase
      .from("profiles") as any)
      .update({ username: trimmed })
      .eq("id", user.id);

    if (dbError) {
      if (dbError.message?.includes("duplicate") || dbError.code === "23505") {
        setError(t("usernameSetup.taken"));
      } else {
        setError(dbError.message || t("common.error"));
      }
      setLoading(false);
      return;
    }

    await refreshProfile();
    toast.success(t("usernameSetup.saved"));
    setLoading(false);
    onComplete();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
          {t("usernameSetup.chooseUsername")}
        </label>
        <div className="relative">
          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(""); }}
            maxLength={24}
            className="w-full pl-9 pr-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="cool_username"
            autoFocus
          />
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">{t("usernameSetup.hint")}</p>
        {error && <p className="text-xs text-destructive mt-1">{error}</p>}
      </div>
      <button
        type="submit"
        disabled={loading || value.trim().length < 3}
        className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : currentUsername ? t("usernameSetup.updateUsername") : t("usernameSetup.setUsername")}
      </button>
    </form>
  );
};

export default UsernameSetup;
